import { Box, Button, Chip, createStyles, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormHelperText, IconButton, Input, InputLabel, Link, List, ListItem, ListItemSecondaryAction, ListItemText, ListSubheader, makeStyles, MenuItem, Select, TextField, Theme } from '@material-ui/core'
import Fab from '@material-ui/core/Fab'
import InputBase from '@material-ui/core/InputBase'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import { Edit, PictureAsPdf, Timer } from '@material-ui/icons'
import Business from '@material-ui/icons/Business'
import Delete from '@material-ui/icons/Delete'
import { Alert } from '@material-ui/lab'
import { Subscribe } from '@react-rxjs/core'
import { convertFromRaw, convertToRaw, EditorState, RawDraftContentState } from 'draft-js'
import firebase from 'firebase/app'
import { useSnackbar } from 'notistack'
import React, { useMemo, useRef, useState } from 'react'
import { Route, Link as RouterLink, Switch } from 'react-router-dom'
import RichTextEditor from 'react-rte'

import { Collections } from 'Collections'
import { useAppUser } from 'components/AppUserProvider'
import { FullscreenLoading } from 'components/FullscreenLoading'
import { ImageButton } from 'components/ImageButton'
import { useOrg, useUnselectOrg } from 'components/OrgProvider'
import { useOrgUser } from 'components/OrgUserProvider'
import { useOrgUsers } from 'components/OrgUsersProvider'
import { PageNavigation } from 'components/PageNavigation'
import { PaymentManager } from 'components/PaymentManager'
import { ProfileSaveButton } from 'components/ProfileSaveButton'
import { RichTextEditorWithToolbar, useRichTextValue } from 'components/RichTextEditorWithToolbar'
import { FirebaseUtils } from 'firebaseUtils/FirebaseUtils'
import { useSyncedState } from 'hooks/useSyncedState'
import { Address, addressFrom } from 'models/Address'
import { AnalyticsEvent } from 'models/AnalyticsEvent'
import { AppRoles } from 'models/AppRoles'
import { AppUser } from 'models/AppUser'
import { avatarImageFrom } from 'models/AvatarImage'
import { Creator } from 'models/Creator'
import { emailFrom, noEmailText } from 'models/Email'
import { DbDoc } from 'models/FbDoc'
import { IndustryTypes } from 'models/IndustryTypes'
import { nameFrom } from 'models/Name'
import { Nullable } from 'models/Nullable'
import { Offer } from 'models/Offer'
import { Org } from 'models/Org'
import { OrgRoles } from 'models/OrgRoles'
import { noOrgUserNameText, OrgUser, orgUserIdFrom } from 'models/OrgUser'
import { phoneFrom } from 'models/Phone'
import { PriceFieldCategories } from 'models/PriceFieldCategories'
import { Rooms } from 'models/Rooms'
import { TextValue, textValueFrom } from 'models/TextValue'
import { Paths } from 'Paths'
import { OfferPdfViewer, pdfData$ } from 'Util/generateOfferPdf'
import { handleEnterKey } from 'Util/handleEnterKey'
import { isEmpty } from 'Util/isEmpty'

import { EmptyPage } from './EmptyPage'
import { NumberInputModal } from './Offer/CreateOffer/OfferRoomEditor'
import { formatMoney } from './Offer/CreateOffer/SummaryTable'
import { hasChanges as objectHasChanges } from './PriceField/PriceFieldScreen'
import { WarningModal } from './Users/OrgUserScreen'

import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css"

type DeleteModalProps = {
    onDelete: () => void
    onClose: () => void
}

function DeleteModal({ onDelete, onClose }: DeleteModalProps) {
    return (
        <Dialog
            open
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">Slett organisasjonen</DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    Er du sikker på at du vil slette organisasjonen din?
                    Dette kan ikke angres og du mister alle brukere, anbud og prisfelter!
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => {
                    onDelete()
                }} color="secondary" autoFocus>Ok</Button>
                <Button onClick={onClose} color="primary">Avbryt</Button>
            </DialogActions>
        </Dialog>
    )
}


type AddressModalProps = {
    onClose: (address: Nullable<Address>) => void
    title: string
    onCancel: () => void
    value: Nullable<Address>
}

export function AddressModal({ onClose, onCancel, title, value }: AddressModalProps) {
    const [street, setStreet] = useState(value?.street || '')
    const [houseNumber, setHouseNumber] = useState(value?.houseNumber || '')
    const [entrance, setEntrance] = useState(value?.entrance || '')
    const [postalCode, setPostalCode] = useState(value?.postalCode || '')
    const [postalArea, setPostalArea] = useState(value?.postalArea || '')

    //TODO: Validation
    const isValid = true

    function onComplete() {
        if (isValid) {
            onClose(addressFrom(
                street,
                houseNumber,
                entrance,
                postalCode,
                postalArea,
            ))
        }
    }

    const houseNumberRef = useRef<any>()
    const entranceRef = useRef<any>()
    const postalCodeRef = useRef<any>()
    const postalAreaRef = useRef<any>()

    return (
        <Dialog
            open
            onClose={onCancel}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
            <DialogContent>
                <List>
                    <ListItem>
                        <TextField
                            type="text"
                            variant="outlined"
                            value={street}
                            onChange={e => setStreet(e.target.value)}
                            placeholder="Gate"
                            autoFocus
                            fullWidth
                            onKeyPress={handleEnterKey(() => houseNumberRef.current?.focus())}
                        />
                    </ListItem>
                    <ListItem>
                        <TextField
                            type="text"
                            variant="outlined"
                            value={houseNumber}
                            onChange={e => setHouseNumber(e.target.value)}
                            placeholder="Hus nr."
                            fullWidth
                            ref={houseNumberRef}
                            onKeyPress={handleEnterKey(() => entranceRef.current?.focus())}
                        />
                    </ListItem>
                    <ListItem>
                        <TextField
                            type="text"
                            variant="outlined"
                            value={entrance}
                            onChange={e => setEntrance(e.target.value)}
                            placeholder="Inngang"
                            fullWidth
                            ref={entranceRef}
                            onKeyPress={handleEnterKey(() => postalCodeRef.current?.focus())}

                        />
                    </ListItem>
                    <ListItem>
                        <TextField
                            type="text"
                            variant="outlined"
                            value={postalCode}
                            onChange={e => setPostalCode(e.target.value)}
                            placeholder="Postnummer"
                            fullWidth
                            ref={postalCodeRef}
                            onKeyPress={handleEnterKey(() => postalAreaRef.current?.focus())}

                        />
                    </ListItem>
                    <ListItem>
                        <TextField
                            type="text"
                            variant="outlined"
                            value={postalArea}
                            onChange={e => setPostalArea(e.target.value)}
                            placeholder="Post sted"
                            fullWidth
                            ref={postalAreaRef}
                            onKeyPress={handleEnterKey(onComplete)}

                        />
                    </ListItem>
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Avbryt</Button>
                <Button disabled={!isValid} onClick={onComplete} color="primary">{'Ok'}</Button>
            </DialogActions>
        </Dialog>
    )
}

type AddressInputProps = {
    value: Nullable<Address>
    onChange: (value: Nullable<Address>) => void
}

export function AddressInput({ value, onChange }: AddressInputProps) {
    const [showAddressModal, setShowAddressModal] = useState(false)
    return (
        <>
            <TextField
                type="text"
                variant="outlined"
                value={value?.addressString || ''}
                placeholder="Adresse"
                fullWidth
                onClick={() => setShowAddressModal(true)}
                onChange={() => setShowAddressModal(true)}
            />
            {showAddressModal ? (
                <AddressModal
                    title="Addresse"
                    value={value}
                    onCancel={() => setShowAddressModal(false)}
                    onClose={(newAddress) => {
                        onChange(newAddress)
                        setShowAddressModal(false)
                    }}
                />
            ) : null}
        </>
    )
}

function creatorFrom(orgUser: OrgUser, appUser: AppUser): Creator {
    return {
        appUser,
        orgUser,
    }
}

function updateFieldsFrom(orgUser: OrgUser, appUser: AppUser) {
    return {
        createdBy: creatorFrom(orgUser, appUser),
        updatedBy: creatorFrom(orgUser, appUser),
        createdAt: firebase.firestore.Timestamp.now(),
        updatedAt: firebase.firestore.Timestamp.now(),
    }
}

function useOfferWithTerms(orgUser: OrgUser | null, appUser: AppUser | null, offerTerms: RawDraftContentState, orgNr: Nullable<string>, address: Address | null, logoUrl: Nullable<string>, name: string): DbDoc<Offer> | null {
    return useMemo(() => {
        if (!orgUser || !appUser) {
            return null
        }

        const org: Org = {
            id: orgUser.org.id,
            address,
            logo: avatarImageFrom(logoUrl),
            name: textValueFrom(name),
            orgNr: textValueFrom(orgNr),
            offerTerms,
            ...updateFieldsFrom(orgUser, appUser),
            owner: {
                appUser,
                orgUser: null,
            },
            hourRate: null,
            industryTypes: { map: {} },
        }

        const offer: Offer = {
            id: "Moti2coQNsniCsocVPu4",
            price: {
                finalPrice: 3300 - 200,
                subTotal: 3300,
                discount: 200,
                manualPrice: null,
            },
            address: addressFrom(
                "Eksempelvegen",
                "10",
                null,
                '7052',
                'Trondheim',
            ),
            contactInfo: {
                email: emailFrom("ola.normann@gmail.com"),
                name: nameFrom("Ola", "Normann"),
                phone: phoneFrom("12345678"),
            },
            ...updateFieldsFrom(orgUser, appUser),
            comment: '',
            customPriceFields: {

            },
            hideUnitPrices: false,
            offerCreator: {
                orgUser,
                appUser,
            },
            offerRoomComments: {
                DkcfdPpZ48cm3cAZ6bh3: 'I gang 1. etasje',
            },
            offerRoomNames: {
                DkcfdPpZ48cm3cAZ6bh3: 'Sikringsskap',
            },
            offerRooms: {
                u5f6MfgOV6Opwm74hXTP: {
                    id: "u5f6MfgOV6Opwm74hXTP",
                    room: Rooms.Flat.ELECTRICAL_OTHER,
                    priceFields: {
                        "3hpSl8trSYrY9NUlbnuP": {
                            id: "3hpSl8trSYrY9NUlbnuP",
                            count: 1,
                            priceField: {
                                id: "ihWSth4FzYvc95FY1SeD",
                                ...updateFieldsFrom(orgUser, appUser),
                                name: textValueFrom('Ny kurs 10/15/16A inntil 15m eks stikk ink sikring') as TextValue,
                                org,
                                price: 3300,
                                industryTypes: { map: {} },
                                type: null,
                                expectedTime: null,
                                materialPrice: null,
                                categories: {
                                    map: {
                                        [PriceFieldCategories.FlatEnum.ELECTRICAL_SUPPLY]: true,
                                    },
                                },
                                subCategories: {
                                    map: {

                                    },
                                },
                                template: null,
                            },
                        },
                    },
                },
            },
            org,
            industryTypes: {
                map: {
                    [IndustryTypes.FlatEnum.ELECTRICAL]: true
                }
            }
        }
        return {
            data: offer,
            metadata: {
                updatedAt: firebase.firestore.Timestamp.now(),
                createdAt: firebase.firestore.Timestamp.now(),
                updatedBy: {
                    appUser,
                    orgUser,
                },
                createdBy: {
                    appUser,
                    orgUser,
                },
            }
        }
    }, [offerTerms, orgUser, appUser, orgNr, address, logoUrl, name])
}

const emptyEditorString = RichTextEditor.createEmptyValue().toString('html')

const useStyles = makeStyles((theme: Theme) => createStyles({
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
        maxWidth: 300,
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    chip: {
        margin: 2,
    },
    noLabel: {
        marginTop: theme.spacing(3),
    },
}),
);

export const ITEM_HEIGHT = 48;
export const ITEM_PADDING_TOP = 8;
export const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

export function HourRateInput({ orgUser, appUser, org, }: {
    orgUser: OrgUser
    appUser: AppUser
    org: Org
}) {
    const { enqueueSnackbar } = useSnackbar()
    const [showModal, setShowModal] = useState(false)

    function onSave(hourRate: number) {
        FirebaseUtils.updateDocument<Org>(
            true,
            Collections.ORGS,
            org.id,
            appUser,
            orgUser,
            AnalyticsEvent.UPDATED_ORG,
            AnalyticsEvent.UPDATED_OWN_ORG,
            AnalyticsEvent.UPDATE_ORG_ERROR,
            enqueueSnackbar,
            'Vellykket!',
            {
                hourRate: isNaN(hourRate) ? null : { default: hourRate },
            },
            () => ({}),
        )
        setShowModal(false)
    }

    const savedRate = org?.hourRate?.default

    const canEdit = orgUser.orgRoles.map[OrgRoles.FlatEnum.OWNER]

    return (
        <>
            {isEmpty(savedRate) ? (
                canEdit ? (
                    <ListSubheader disableSticky>{'Legg til'}
                        {false /*isMobile*/ ? (
                            <IconButton size="small" color="secondary" onClick={() => {
                                setShowModal(true)
                            }}>
                                {<Timer />}
                            </IconButton>
                        ) : (
                            <Box mx={1} display="inline">
                                <Button size="small" color="secondary" variant="outlined" endIcon={<Timer />} onClick={() => {
                                    setShowModal(true)
                                }}>
                                    Timelønn montør med avgifter
                                </Button>
                            </Box>
                        )}
                    </ListSubheader>
                ) : null
            ) : (
                // @ts-ignore
                <ListItem dense button={canEdit || undefined} onClick={canEdit ? () => setShowModal(true) : undefined}>
                    <ListItemText
                        primary="Timelønn montør med avgifter"
                        secondary={formatMoney(savedRate)}
                    />
                    {canEdit ? (
                        <ListItemSecondaryAction>
                            <IconButton edge="end" color="secondary" onClick={() => setShowModal(true)}>
                                <Edit />
                            </IconButton>
                        </ListItemSecondaryAction>
                    ) : null}
                </ListItem>
            )}
            {showModal ? (
                <NumberInputModal
                    returnNan
                    onClose={onSave}
                    title="Velg Timelønn"
                    onCancel={() => setShowModal(false)}
                    initialValue={savedRate ?? NaN}
                    extraAction={
                        isEmpty(savedRate) ? null : (
                            <Button color="secondary" onClick={() => onSave(NaN)}>Fjern</Button>
                        )
                    }
                >
                    <ListItem disableGutters>
                        <Alert style={{ width: '100%' }} severity="info">
                            Dette blir lagret i organisasjonen din og gjelder for alle anbud som tar i bruk prisfelter med monteringstid
                        </Alert>
                    </ListItem>
                </NumberInputModal>
            ) : null}
        </>
    )
}

export function TransferOrgOwnerModal() {
    const { enqueueSnackbar } = useSnackbar()
    const [currentOrgUser] = useOrgUser()
    const [currentAppUser] = useAppUser()
    const [org] = useOrg()
    const isAdmin = !!currentAppUser?.data.appRoles.map[AppRoles.FlatEnum.ADMIN]
    const isOrgOwner = org?.data.owner.appUser?.id === currentOrgUser?.data.userId
    const [showTransferOrgOwnerModal, setShowTransferOrgOwnerModal] = useState(false)
    const [orgUsers] = useOrgUsers()
    const prospects = orgUsers?.filter(orgUser => orgUser.data.userId !== currentOrgUser?.data.userId)

    return ((isAdmin && currentAppUser && isOrgOwner && org && currentOrgUser && prospects) ? (
        <>
            <Button
                color="secondary"
                size="small"
                onClick={() => {
                    setShowTransferOrgOwnerModal(true)
                }}
            >
                Endre eier
            </Button>
            {showTransferOrgOwnerModal ? (
                <WarningModal
                    title="Overfør eierskap"
                    onCancel={() => setShowTransferOrgOwnerModal(false)}
                    onClose={() => setShowTransferOrgOwnerModal(false)}
                >
                    {prospects.length === 0 ? (
                        <Typography>Du er det eneste medlemet av denne organisasjonen. Du må <Link component={RouterLink} to={Paths.ORG_USERS}>invitere noen</Link> for å kunne overføre eierskapet</Typography>
                    ) : (
                        <Typography variant="body1">
                            Trykk på en bruker for å overføre eierskap. Etter dette er gjort kan kun den personen som har eierskap av organisasjonen overføre eierskapet tilbake!
                        </Typography>
                    )}
                    <List subheader={<ListSubheader>Org Brukere</ListSubheader>}>
                        {prospects.map(({ data: nextOrgUser }) => (
                            <ListItem
                                key={nextOrgUser.id}
                                dense
                                button
                                onClick={async () => {
                                    const orgUser = await FirebaseUtils.getDocument<OrgUser>(false, Collections.ORG_USERS, nextOrgUser.id, null, nextOrgUser, AnalyticsEvent.FETCH_ORG_USER_ERROR, enqueueSnackbar, () => ({ orgUserId: nextOrgUser.id }))
                                    const appUser = await FirebaseUtils.getDocument<AppUser>(false, Collections.APP_USERS, nextOrgUser.userId, null, nextOrgUser, AnalyticsEvent.GET_APP_USER_ERROR, enqueueSnackbar, () => ({ orgUserId: nextOrgUser.id }))
                                    if (orgUser && appUser) {
                                        FirebaseUtils.updateDocument<OrgUser>(
                                            true,
                                            Collections.ORG_USERS,
                                            orgUser.data.id,
                                            currentAppUser.data,
                                            currentOrgUser.data,
                                            AnalyticsEvent.ADDED_ORG_ROLE,
                                            AnalyticsEvent.TRANSFERED_ORG_OWNERSHIP,
                                            AnalyticsEvent.UPDATE_ORG_USER_ERROR,
                                            enqueueSnackbar,
                                            null,
                                            {
                                                orgRoles: {
                                                    map: {
                                                        [OrgRoles.FlatEnum.OWNER]: true,
                                                    }
                                                }
                                            },
                                            () => ({}),
                                        )
                                        FirebaseUtils.updateDocument<Org>(
                                            true,
                                            Collections.ORGS,
                                            org.data.id,
                                            currentAppUser.data,
                                            currentOrgUser.data,
                                            AnalyticsEvent.TRANSFERED_ORG_OWNERSHIP,
                                            AnalyticsEvent.TRANSFERED_ORG_OWNERSHIP,
                                            AnalyticsEvent.TRANSFER_ORG_OWNERSHIP_ERROR,
                                            enqueueSnackbar,
                                            'Organisasjonen har blitt overført til en ny eier!',
                                            {
                                                owner: {
                                                    appUser: appUser.data,
                                                    orgUser: orgUser.data,
                                                }
                                            },
                                            () => ({
                                                nextOwnerUserId: appUser.data.id,
                                                previousOwnerUserId: currentAppUser.data.id,
                                            }),
                                        )
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={noOrgUserNameText(nextOrgUser.orgContactInfo?.name?.fullName)}
                                    secondary={noEmailText(nextOrgUser.orgContactInfo?.email?.email)}
                                />
                            </ListItem>
                        ))}
                    </List>
                </WarningModal>
            ) : null}
        </>
    ) : null)
}

export function MyOrgScreen() {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const [orgUser, loadingOrgUser] = useOrgUser()
    const [appUser, loadingAppUser] = useAppUser()
    const [org, loadingOrg] = useOrg()
    const [unselectOrg] = useUnselectOrg()
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [owner, loadingOwner] = FirebaseUtils.useDocumentData<OrgUser>(true, Collections.ORG_USERS, org && org.data.owner.appUser && orgUserIdFrom(org.data.id, org.data.owner.appUser.id), AnalyticsEvent.FETCH_ORG_USER_ERROR)

    const [name, setName] = useSyncedState(org?.data.name?.text || '')
    const [orgNr, setOrgNr] = useSyncedState(org?.data.orgNr?.text || '')
    const [address, setAddress] = useSyncedState(org?.data.address || null)
    const [logoUrl, setLogoUrl] = useSyncedState(org?.data.logo?.original.url || null)
    const [industryTypes, setIndustryTypes] = useSyncedState(org?.data.industryTypes.map || {})
    const [showAddressModal, setShowAddressModal] = useState(false)

    const [editorValue, setEditorValue] = useRichTextValue(org?.data.offerTerms)

    const savedOfferTermsAsHtml = useMemo(() => RichTextEditor.createEmptyValue().setEditorState(org?.data.offerTerms ? EditorState.createWithContent(convertFromRaw(org?.data.offerTerms)) : EditorState.createEmpty()).toString('html'), [org])

    const editorContent = editorValue.getEditorState().getCurrentContent()

    const offerTerms = useMemo(() => convertToRaw(editorContent), [editorContent])

    const offerWithTerms = useOfferWithTerms(orgUser?.data || null, appUser?.data || null, offerTerms, orgNr, address, logoUrl, name)

    const hasChanges =
        name !== (org?.data.name?.text || '')
        || orgNr !== (org?.data.orgNr?.text || '')
        || address?.addressString !== org?.data.address?.addressString
        || (logoUrl !== (org?.data.logo?.original.url || null))
        || editorValue.toString('html') !== savedOfferTermsAsHtml
        || objectHasChanges(org?.data.industryTypes.map, industryTypes)
        || objectHasChanges(industryTypes, org?.data.industryTypes.map)

    //TODO: Validation
    const isValid = hasChanges

    const loading = loadingOwner || loadingOrg || loadingOrgUser || loadingAppUser

    const isOrgOwner = org?.data.owner.appUser?.id === orgUser?.data.userId

    const deleteModal = (deleteModalOpen) ? (
        <DeleteModal
            onClose={() => setDeleteModalOpen(false)}
            onDelete={() => {
                if (org && orgUser && !!appUser) {
                    FirebaseUtils.deleteDocument(
                        true,
                        Collections.ORGS,
                        org.data.id,
                        appUser.data,
                        orgUser.data,
                        AnalyticsEvent.DELETED_ORG,
                        AnalyticsEvent.DELETED_ORG,
                        AnalyticsEvent.DELETE_ORG_ERROR,
                        enqueueSnackbar,
                        'Slettet organisasjon',
                        () => ({
                            orgId: org.data.id,
                            deletedByAppUserId: orgUser.data.userId,
                            deletedByOrgUserId: orgUser.data.id,
                        }),
                    )
                    FirebaseUtils.deleteDocument(
                        true,
                        Collections.ORG_USERS,
                        orgUser.data.id,
                        appUser.data,
                        orgUser.data,
                        AnalyticsEvent.DELETED_OWN_ORG_USER,
                        AnalyticsEvent.DELETED_ORG,
                        AnalyticsEvent.DELETE_ORG_USER_ERROR,
                        enqueueSnackbar,
                        'Slettet Org Bruker',
                    )
                    unselectOrg(AnalyticsEvent.DELETED_ORG)
                }
            }}
        />
    ) : null

    function save() {
        if (!org) {
            console.error('No org state!')
            return
        }
        if (!orgUser) {
            console.error('No orgUser state!')
            return
        }
        if (!appUser) {
            console.error('No orgUser state!')
            return
        }
        if (isValid) {
            FirebaseUtils.updateDocument<Org>(
                true,
                Collections.ORGS,
                org.data.id,
                appUser.data,
                orgUser.data,
                AnalyticsEvent.UPDATED_ORG,
                AnalyticsEvent.UPDATED_OWN_ORG,
                AnalyticsEvent.UPDATE_ORG_ERROR,
                enqueueSnackbar,
                'Vellykket!',
                {
                    ...org.data,
                    name: textValueFrom(name),
                    orgNr: textValueFrom(orgNr),
                    address,
                    offerTerms: editorValue.toString('html') === emptyEditorString ? null : offerTerms,
                    logo: avatarImageFrom(logoUrl),
                    industryTypes: {
                        map: industryTypes,
                    },
                    hourRate: org?.data.hourRate,
                },
                () => ({}),
                {
                    useSet: true,
                },
            )

        }
    }

    if (loading) {
        return <FullscreenLoading screenId="Min Org" />
    }

    const card = (
        <Paper
            elevation={3}
            style={{
                position: 'relative',
                //width: 300,
                //height: 300,
                borderRadius: 18,
                display: 'flex',
                justifyContent: 'flex-start',
                flexDirection: 'column',
                alignItems: 'center',
                paddingLeft: 20,
                paddingRight: 20,
                margin: 20,
            }}
        >
            {isOrgOwner ? (
                <Fab
                    size="medium"
                    style={{ position: 'absolute', bottom: 40, right: -16 }}
                    onClick={() => setDeleteModalOpen(true)}
                    color="secondary"
                    aria-label="delete"
                >
                    <Delete />
                </Fab>
            ) : null}
            <Fab
                component={RouterLink}
                to={Paths.MY_ORG_OFFER_TERMS}
                style={{
                    position: 'absolute',
                    zIndex: 99,
                    top: 40,
                    right: -16,
                }}
                color="secondary"
                aria-label="edit-offer-terms"
                size="medium"
            >
                <PictureAsPdf />
            </Fab>
            <ImageButton label="Org Logo" value={logoUrl} onChange={setLogoUrl}>
                <Business />
            </ImageButton>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    marginTop: 18,
                    marginBottom: 18,
                }}
            >
                <InputBase
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Org navn"
                    inputProps={{
                        'aria-label': 'org name input',
                        style: {
                            fontSize: 26,
                            fontWeight: 'bold',
                            textAlign: 'center',
                        },
                    }}
                />
                <InputBase
                    value={orgNr}
                    placeholder="OrgNr"
                    onChange={e => setOrgNr(e.target.value)}
                    type="number"
                    inputProps={{
                        'aria-label': 'org number input',
                        style: {
                            fontSize: 16,
                            fontWeight: 'bold',
                            textAlign: 'center',
                        },
                    }}
                />
                <InputBase
                    value={address?.addressString || ''}
                    onClick={() => setShowAddressModal(true)}
                    onChange={() => setShowAddressModal(true)}
                    placeholder="Org Adresse"
                    inputProps={{
                        'aria-label': 'org address input',
                        style: {
                            fontSize: 16,
                            fontWeight: 'bold',
                            textAlign: 'center',
                        },
                    }}
                />
                {showAddressModal ? (
                    <AddressModal
                        title="Addresse"
                        value={address}
                        onCancel={() => setShowAddressModal(false)}
                        onClose={(newAddress) => {
                            setAddress(newAddress)
                            setShowAddressModal(false)
                        }}
                    />
                ) : null}
                <Typography variant="h6">
                    <Link component={RouterLink} to={isOrgOwner ? Paths.MY_ORG_USER : Paths.ORG_USERS + '/' + owner?.data.id}>
                        {noOrgUserNameText(owner?.data.orgContactInfo?.name?.fullName || owner?.data.personalContactInfo?.name?.fullName)}
                    </Link>
                </Typography>
                <TransferOrgOwnerModal />
                {false ? (
                    <FormControl fullWidth className={classes.formControl}>
                        <InputLabel id="industry-type-mutiple-chip-label">Bransjer</InputLabel>
                        <Select
                            labelId="industry-type-mutiple-chip-label"
                            id="industry-type-mutiple-chip"
                            multiple
                            disabled
                            value={Object.keys(industryTypes)}
                            onChange={(e) => setIndustryTypes((e.target.value as IndustryTypes.IndustryType[]).reduce((acc, v) => ({
                                ...acc,
                                [v]: true,
                            }), {}))}
                            input={<Input id="select-multiple-chip" />}
                            renderValue={(selected) => (
                                <div className={classes.chips}>
                                    {(selected as IndustryTypes.IndustryType[]).map((value) => (
                                        <Chip key={value} label={IndustryTypes.Flat[value].name} className={classes.chip} />
                                    ))}
                                </div>
                            )}
                            fullWidth
                            MenuProps={MenuProps}
                        >
                            {IndustryTypes.FlatList.map((description) => (
                                <MenuItem key={description.id} value={description.id}>
                                    {description.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : null}
                <FormControl fullWidth>
                    <InputLabel htmlFor="industry-type-native-simple">Bransje</InputLabel>
                    <Select
                        labelId="industry-type-mutiple-chip-label"
                        id="industry-type-mutiple-chip"
                        input={<Input id="select-multiple-chip" />}
                        MenuProps={MenuProps}
                        value={Object.keys(industryTypes).length ? Object.keys(industryTypes)[0] : ''}
                        onChange={(e) => setIndustryTypes({ [e.target.value as IndustryTypes.IndustryType]: true })}
                        fullWidth
                        required
                        disabled={!!Object.keys(industryTypes).length}
                        error={!Object.keys(industryTypes).length}
                        variant="outlined"
                        placeholder="Velg en Bransje"
                        inputProps={{
                            name: 'bransje',
                            id: 'industry-type-native-simple',
                        }}
                    >
                        <MenuItem key="EMPTY" value="">Ingen</MenuItem>
                        {IndustryTypes.FlatList.map((description) => (
                            <MenuItem key={description.id} value={description.id}>
                                {description.name}
                            </MenuItem>
                        ))}
                    </Select>
                    {!Object.keys(industryTypes).length && <FormHelperText error>Velg en Bransje NB! Dette kan du bare gjøre en gang!</FormHelperText>}
                </FormControl>
                <div style={{ margin: 18, display: 'flex', alignItems: 'center' }}>
                </div>
            </div>
            <ProfileSaveButton
                hasChanges={hasChanges}
                save={save}
                isValid={isValid}
            />
        </Paper>
    )

    return (
        <PageNavigation
            title="Min Org"
            backHistoryButton
            hasUnsavedChanges={hasChanges}
            safeUnsavedNavigation={[Paths.MY_ORG, Paths.MY_ORG_OFFER_TERMS]}
        >
            {deleteModal}
            {org ? (
                <Switch>
                    <Route exact path={Paths.MY_ORG}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                marginTop: 50,
                                marginBottom: 10,
                                marginLeft: 'auto',
                                marginRight: 'auto',
                            }}
                        >
                            {card}
                        </div>
                        <List>
                            <PaymentManager />
                            {(org && orgUser && appUser) ? (
                                <HourRateInput orgUser={orgUser.data} org={org.data} appUser={appUser.data} />
                            ) : null}
                            <ListItem>
                                <RichTextEditorWithToolbar value={editorValue} onChange={setEditorValue} />
                            </ListItem>
                        </List>
                    </Route>
                    <Route path={Paths.MY_ORG_OFFER_TERMS}>
                        <Subscribe source$={pdfData$}>
                            {offerWithTerms ? (
                                <OfferPdfViewer offer={offerWithTerms} scale={1} />
                            ) : null}
                        </Subscribe>
                    </Route>
                </Switch>
            ) : (
                <EmptyPage text="Finner ikke organisasjonen" />
            )}
        </PageNavigation >
    )
}
